import { LoadingState } from "@/components/ui/loading-state";
import { useMemo } from "react";

import type {
  BuilderCoverageCategory,
  BuilderCoveragePeriodStatus,
  BuilderCoverageResponse,
} from "@/lib/api/databook";

import {
  CoverageStage,
  type CoverageCellStatus,
  type CoverageStageCell,
  type CoverageStageGap,
  type CoverageStageRow,
} from "./CoverageStage";
import { normalizeCoveragePeriods } from "./coveragePeriodSemantics";

interface SourceCoverageStageProps {
  coverage: BuilderCoverageResponse | null;
  startFiscalYear: number;
  endFiscalYear: number;
  loading?: boolean;
  error?: boolean;
  onBack: () => void;
  onProceed: () => void;
  onRetry?: () => void;
  onCreateRfi?: () => void;
  onUploadRfi?: () => void;
  backLabel?: string;
  proceedLabel?: string;
}

interface CoverageColumn {
  year: number;
  label: string;
  months: string[];
}

const CORE_SOURCE_CATEGORIES = new Set([
  "pnl",
  "balance_sheet",
]);

const UI_FONT = {
  fontFamily:
    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

function fiscalYearForMonth(value: string, financialYearEndMonth: number): number {
  const [year, month] = value.split("-").map(Number);
  return year + (month > financialYearEndMonth ? 1 : 0);
}

function fiscalMonths(year: number, financialYearEndMonth: number): string[] {
  const months = [
    ...Array.from(
      { length: 12 - financialYearEndMonth },
      (_, index) => financialYearEndMonth + index + 1,
    ),
    ...Array.from({ length: financialYearEndMonth }, (_, index) => index + 1),
  ];
  return months.map((month) => {
    const calendarYear = year - Number(month > financialYearEndMonth);
    return `${calendarYear}-${String(month).padStart(2, "0")}`;
  });
}

function fiscalYearLabel(year: number): string {
  return `FY${String(year).slice(-2)}`;
}

function coverageColumns(
  financialYearEndMonth: number,
  startFiscalYear: number,
  endFiscalYear: number,
): CoverageColumn[] {
  return Array.from(
    { length: endFiscalYear - startFiscalYear + 1 },
    (_, index) => {
      const year = startFiscalYear + index;
      return {
        year,
        label: fiscalYearLabel(year),
        months: fiscalMonths(year, financialYearEndMonth),
      };
    },
  );
}

function displayStatus(
  status: BuilderCoveragePeriodStatus | undefined,
): CoverageCellStatus {
  if (!status || status === "missing") return "missing";
  return "full";
}

function monthlyCells(
  category: BuilderCoverageCategory,
  columns: CoverageColumn[],
): CoverageStageCell[] {
  const periods = new Map(category.months.map((period) => [period.value, period]));
  return columns.map((column) => {
    const months = column.months.map((month) =>
      displayStatus(periods.get(month)?.status),
    );
    return { months };
  });
}

function annualCells(
  category: BuilderCoverageCategory,
  columns: CoverageColumn[],
): CoverageStageCell[] {
  const periods = new Map(
    category.years.map((period) => [Number(period.value), period]),
  );
  return columns.map((column) => ({
    status: displayStatus(periods.get(column.year)?.status),
  }));
}

function categoryRows(
  category: BuilderCoverageCategory,
  columns: CoverageColumn[],
): CoverageStageRow[] {
  const type = category.source_category === "pnl"
    || category.source_category === "balance_sheet"
    ? `${category.label} (Management accounts)`
    : category.label;
  if (category.months.length > 0) {
    return [
      {
        id: `${category.source_category}:monthly`,
        type,
        basis: "Monthly",
        cells: monthlyCells(category, columns),
      },
    ];
  }
  if (category.years.length > 0) {
    return [
      {
        id: `${category.source_category}:annual`,
        type,
        basis: "Annual",
        cells: annualCells(category, columns),
      },
    ];
  }
  return [
    {
      id: `${category.source_category}:unknown`,
      type,
      basis: "Period not identified",
      cells: columns.map(() => ({ status: "missing" })),
    },
  ];
}

function displayMonth(value: string): string {
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function displayMonthRanges(values: string[]): string[] {
  const periods = [...new Set(values)].sort();
  const ranges: string[] = [];
  let rangeStart = periods[0];
  let previous = periods[0];

  const monthIndex = (value: string) => {
    const [year, month] = value.split("-").map(Number);
    return year * 12 + month;
  };

  for (const period of periods.slice(1)) {
    if (monthIndex(period) === monthIndex(previous) + 1) {
      previous = period;
      continue;
    }
    ranges.push(
      rangeStart === previous
        ? displayMonth(rangeStart)
        : `${displayMonth(rangeStart)}–${displayMonth(previous)}`,
    );
    rangeStart = period;
    previous = period;
  }

  if (rangeStart) {
    ranges.push(
      rangeStart === previous
        ? displayMonth(rangeStart)
        : `${displayMonth(rangeStart)}–${displayMonth(previous)}`,
    );
  }
  return ranges;
}

function samePeriods(left: string[], right: string[]): boolean {
  return left.length === right.length
    && left.every((period, index) => period === right[index]);
}

function criticalCoverageGaps(
  coverage: BuilderCoverageResponse,
  startFiscalYear: number,
  endFiscalYear: number,
): CoverageStageGap[] {
  const inScope = (month: string) => {
    const year = fiscalYearForMonth(month, coverage.financial_year_end_month);
    return year >= startFiscalYear && year <= endFiscalYear;
  };
  const pnlMonths = coverage.critical_missing_pnl_months.filter(inScope);
  const balanceSheetMonths = coverage.critical_missing_balance_sheet_months.filter(inScope);

  if (pnlMonths.length > 0 && samePeriods(pnlMonths, balanceSheetMonths)) {
    return [{
      id: "pnl-and-balance-sheet",
      type: "P&L and Balance Sheet",
      periods: displayMonthRanges(pnlMonths),
    }];
  }

  const gaps: CoverageStageGap[] = [];
  if (pnlMonths.length > 0) {
    gaps.push({
      id: "pnl",
      type: "P&L",
      periods: displayMonthRanges(pnlMonths),
    });
  }
  if (balanceSheetMonths.length > 0) {
    gaps.push({
      id: "balance-sheet",
      type: "Balance Sheet",
      periods: displayMonthRanges(balanceSheetMonths),
    });
  }
  return gaps;
}

function coveragePresentation(
  coverage: BuilderCoverageResponse,
  startFiscalYear: number,
  endFiscalYear: number,
) {
  const managementAccounts = coverage.categories.find(
    (category) => category.source_category === "management_accounts",
  );
  const statementCategory = (
    sourceCategory: "pnl" | "balance_sheet",
    label: string,
  ): BuilderCoverageCategory => {
    const existing = coverage.categories.find(
      (category) => category.source_category === sourceCategory,
    );
    return {
      source_category: sourceCategory,
      label: existing?.label ?? label,
      file_count: existing?.file_count ?? managementAccounts?.file_count ?? 0,
      months: [...new Map(
        [...(existing?.months ?? []), ...(managementAccounts?.months ?? [])]
          .map((period) => [period.value, period]),
      ).values()].sort((left, right) => left.value.localeCompare(right.value)),
      years: existing?.years ?? [],
    };
  };
  const projectedCategories = managementAccounts
    ? [
        statementCategory("pnl", "P&L"),
        statementCategory("balance_sheet", "Balance sheet"),
        ...coverage.categories.filter(
          (category) => ![
            "pnl",
            "balance_sheet",
            "management_accounts",
          ].includes(category.source_category),
        ),
      ]
    : coverage.categories;
  const categories = projectedCategories.map((category) => ({
    ...category,
    ...normalizeCoveragePeriods(
      category.source_category,
      category.months,
      category.years,
      coverage.financial_year_end_month,
    ),
  }));
  const columns = coverageColumns(coverage.financial_year_end_month, startFiscalYear, endFiscalYear);
  const coreCategories = categories.filter((category) =>
    CORE_SOURCE_CATEGORIES.has(category.source_category),
  );
  const visibleCategories =
    coreCategories.length > 0
      ? coreCategories
      : categories.slice(0, 7);
  const additionalCategories =
    coreCategories.length > 0
      ? categories.filter(
          (category) => !CORE_SOURCE_CATEGORIES.has(category.source_category),
        )
      : categories.slice(7);
  const criticalGaps = criticalCoverageGaps(coverage, startFiscalYear, endFiscalYear);

  return {
    periods: columns.map((column) => column.label),
    rows: visibleCategories.flatMap((category) =>
      categoryRows(category, columns),
    ),
    additionalRows: additionalCategories.flatMap((category) =>
      categoryRows(category, columns),
    ),
    criticalGaps,
  };
}

export function SourceCoverageStage({
  coverage,
  startFiscalYear,
  endFiscalYear,
  loading = false,
  error = false,
  onBack,
  onProceed,
  onRetry,
  onCreateRfi,
  onUploadRfi,
  backLabel,
  proceedLabel,
}: SourceCoverageStageProps) {
  const presentation = useMemo(
    () => (coverage ? coveragePresentation(coverage, startFiscalYear, endFiscalYear) : null),
    [coverage, startFiscalYear, endFiscalYear],
  );

  if (loading) return <LoadingState />;

  if (error || !coverage || !presentation) {
    return (
      <div className="flex-1 overflow-y-auto" style={UI_FONT}>
        <div className="builder-stage-frame pb-10 pt-6">
          <div className="mb-5 font-semibold" style={{ fontSize: 20 }}>
            Update RFI
          </div>
          <div className="rounded-xl border border-rose-300 bg-card px-4 py-8 text-center">
            <div className="font-semibold">RFI update could not be loaded</div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-4 rounded-lg bg-foreground px-4 py-2 font-medium text-background hover:opacity-90"
                style={{ fontSize: 12.5 }}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <CoverageStage
      {...presentation}
      hasRfi={coverage.has_rfi}
      onBack={onBack}
      onProceed={onProceed}
      onCreateRfi={onCreateRfi}
      onUploadRfi={onUploadRfi}
      backLabel={backLabel}
      proceedLabel={proceedLabel}
    />
  );
}
